import { captureException } from '@sentry/browser'
import { uploadFile, UploadHttpError, UploadNetworkError } from '../utils/upload-file'
import type { UploadFileOptions } from '../utils/upload-file'
import { sendStudioEvent } from './amplitude'

export const UPLOAD_TELEMETRY_TOOLS = {
  aiPhotoStyles: 'AI Photo Styles',
  aiVideoGenerator: 'AI Video Generator',
  aiVideoUpscaler: 'AI Video Upscaler',
} as const

export type UploadTelemetryTool =
  (typeof UPLOAD_TELEMETRY_TOOLS)[keyof typeof UPLOAD_TELEMETRY_TOOLS]

type UploadFailureReason = 'http' | 'network' | 'aborted' | 'unknown'

interface UploadFailure {
  reason: UploadFailureReason
  httpStatus?: number
}

interface UploadFileWithTelemetryOptions extends UploadFileOptions {
  telemetryTool: UploadTelemetryTool
  sessionId: string
  eventProperties?: Record<string, unknown>
}

function describeFailure(error: unknown): UploadFailure {
  if (error instanceof UploadHttpError) {
    return { reason: 'http', httpStatus: error.status }
  }
  if (error instanceof UploadNetworkError) {
    return { reason: 'network' }
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return { reason: 'aborted' }
  }
  return { reason: 'unknown' }
}

function elapsedMs(startedAt: number): number {
  return Math.round(performance.now() - startedAt)
}

/**
 * Not a hook, and reports from the upload rather than from mutation callbacks: callers close
 * dialogs and navigate mid-upload, tearing down React observers while the transfer runs on.
 */
export async function uploadFileWithTelemetry({
  telemetryTool,
  sessionId,
  eventProperties,
  ...uploadOptions
}: UploadFileWithTelemetryOptions): Promise<void> {
  const { file, onProgress } = uploadOptions
  const uploadProperties: Record<string, unknown> = {
    sessionId,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    ...eventProperties,
  }
  const startedAt = performance.now()
  let lastProgressPercent = 0

  sendStudioEvent(`${telemetryTool} - Upload Started`, uploadProperties)

  try {
    await uploadFile({
      ...uploadOptions,
      onProgress: (progress, progressFile) => {
        lastProgressPercent = progress
        onProgress?.(progress, progressFile)
      },
    })
  } catch (error) {
    const failure = describeFailure(error)
    const failureProperties = {
      ...uploadProperties,
      ...failure,
      progressPercent: lastProgressPercent,
      durationMs: elapsedMs(startedAt),
    }

    sendStudioEvent(`${telemetryTool} - Upload Failed`, failureProperties)

    if (failure.reason !== 'aborted') {
      captureException(error, {
        tags: { uploadTool: telemetryTool, uploadFailureReason: failure.reason },
        extra: failureProperties,
      })
    }

    throw error
  }

  sendStudioEvent(`${telemetryTool} - Upload Completed`, {
    ...uploadProperties,
    durationMs: elapsedMs(startedAt),
  })
}
